import { ChangeEvent, FC, MouseEvent, useRef, useState } from "react";
import {
  Button,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from "@mui/material";
import { Download, KeyboardArrowDown, Upload } from "@mui/icons-material";
import { UserDownloadIcon } from "ui";

interface LeadsCsvMenuProps {
  onDownloadTemplate: () => void;
  onImportFile: (file: File) => void;
  onDownloadLeadList: () => void;
  isImporting?: boolean;
  isLeadListDisabled?: boolean;
}

const LeadsCsvMenu: FC<LeadsCsvMenuProps> = ({
  onDownloadTemplate,
  onImportFile,
  onDownloadLeadList,
  isImporting,
  isLeadListDisabled,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const open = Boolean(anchorEl);

  const handleOpen = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDownloadTemplate = () => {
    onDownloadTemplate();
    handleClose();
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
    handleClose();
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) onImportFile(file);
    // Reset so picking the same file again still fires onChange.
    event.target.value = "";
  };

  const handleDownloadLeadList = () => {
    onDownloadLeadList();
    handleClose();
  };

  return (
    <>
      <Button
        id="leads-csv-button"
        variant="outlined"
        onClick={handleOpen}
        endIcon={<KeyboardArrowDown />}
        aria-controls={open ? "leads-csv-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
      >
        Update Leads
      </Button>
      <Menu
        id="leads-csv-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "leads-csv-button",
        }}
      >
        <MenuItem onClick={handleDownloadTemplate}>
          <ListItemIcon>
            <Download fontSize="small" sx={{ color: "#C1C7CE" }} />
          </ListItemIcon>
          <ListItemText>Download CSV Template</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleUploadClick} disabled={isImporting}>
          <ListItemIcon>
            <Upload fontSize="small" sx={{ color: "#C1C7CE" }} />
          </ListItemIcon>
          <ListItemText>
            {isImporting ? "Uploading..." : "Upload CSV File"}
          </ListItemText>
        </MenuItem>
        <MenuItem
          onClick={handleDownloadLeadList}
          disabled={isLeadListDisabled}
        >
          <ListItemIcon>
            <UserDownloadIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Download Lead List</ListItemText>
        </MenuItem>
      </Menu>
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,text/csv"
        hidden
        onChange={handleFileChange}
      />
    </>
  );
};

export default LeadsCsvMenu;
